import { parseAccessToken } from '../jwt';
import { localStorageAuthDataKey } from './authStorageConfig';
/**
 * The amount of time, in seconds, before a token expires where we will consider
 * it expired.
 */
export const authEarlyExpiryTime = 30;
/**
 * The amount of time, in seconds, that an access token is considered valid.
 * Currently set to 600 seconds (10 minutes).
 */
const accessTokenValidTime = 600;
/**
 * The margin of error (in seconds) allowed between the client and server times
 * when checking for time synchronization. If exceeded, a warning will be logged
 * and the client time will be adjusted to match the server time.
 */
const clientServerTimeMarginOfError = 5;
/**
 * Storage class for the auth data.
 *
 * - Stores the auth data in memory and localStorage for later retrieval.
 *
 * This is a private class that should only be used by the AuthManager module.
 */
class AuthStorage {
    constructor() {
        Object.defineProperty(this, "authData", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        /** The (server) time when the access token expires, in seconds. */
        Object.defineProperty(this, "accessTokenExpiresAt", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        /**
         * The difference between client- and server-time, in seconds. Negative if
         * client is ahead of server, positive if server is ahead of client
         */
        Object.defineProperty(this, "clientServerTimeDiff", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
    }
    /**
     * Returns an estimated server time (unix epoch in seconds) by adding the
     * client-server time difference to the current client time.
     *
     * The time-diff is stored whenever a new access token is saved.
     *
     * @returns {number} The estimated server time in seconds.
     */
    estimatedServerTime() {
        // Small difference is expected due to network latency, processing time etc.
        const nowInSeconds = Date.now() / 1000;
        if (this.clientServerTimeDiff >= clientServerTimeMarginOfError ||
            this.clientServerTimeDiff <= -clientServerTimeMarginOfError) {
            // If client and server time were off by more than the margin of error,
            // we assume that the client time is off and adjust the client time to
            // match the server time.
            return Math.ceil(nowInSeconds + this.clientServerTimeDiff);
        }
        return Math.ceil(nowInSeconds);
    }
    /**
     * Sets the auth data in localStorage.
     *
     * @param authData The auth data.
     */
    setAuthData(authData) {
        this.authData = authData;
        const clientTime = Math.ceil(Date.now() / 1000);
        const expectedExpiresAt = clientTime + accessTokenValidTime;
        const jwtExpiresAt = parseAccessToken(authData.access_token).exp;
        this.clientServerTimeDiff = jwtExpiresAt - expectedExpiresAt;
        if (process.env.NODE_ENV !== 'test' &&
            Math.abs(this.clientServerTimeDiff) > clientServerTimeMarginOfError) {
            const aheadOrBehind = this.clientServerTimeDiff > 0 ? 'behind' : 'ahead';
            console.info(`Client time is ${aheadOrBehind} by significant amount. (${Math.abs(this.clientServerTimeDiff)}s)`);
        }
        this.accessTokenExpiresAt = jwtExpiresAt;
        localStorage.setItem(localStorageAuthDataKey, JSON.stringify(authData));
    }
    /** Gets the auth data from memory or localStorage. */
    getAuthData() {
        if (this.authData) {
            return this.authData;
        }
        const authData = localStorage.getItem(localStorageAuthDataKey);
        const parsedAuthData = authData ? JSON.parse(authData) : null;
        if (this.validateAuthDataComplete(parsedAuthData)) {
            this.authData = parsedAuthData;
        }
        return parsedAuthData;
    }
    /** Removes the auth data from localStorage. */
    removeAuthData() {
        this.authData = null;
        localStorage.removeItem(localStorageAuthDataKey);
    }
    getAccessToken() {
        var _a;
        return ((_a = this.authData) === null || _a === void 0 ? void 0 : _a.access_token) || null;
    }
    getRefreshToken() {
        var _a;
        return ((_a = this.authData) === null || _a === void 0 ? void 0 : _a.refresh_token) || null;
    }
    /**
     * Validates that the auth data is complete - i.e not fragmented.
     *
     * @param authData The auth data.
     */
    validateAuthDataComplete(authData) {
        if (!authData) {
            return false;
        }
        const keys = [
            'access_token',
            'refresh_token',
            'expires_in',
        ];
        return keys.every((key) => typeof authData[key] !== 'undefined');
    }
    isAccessTokenExpired() {
        const accessToken = this.getAccessToken();
        if (!accessToken)
            return true;
        // Subtract grace-period to account for time skew
        const expiryTime = this.accessTokenExpiresAt - authEarlyExpiryTime;
        return this.estimatedServerTime() >= expiryTime;
    }
    isRefreshTokenExpired(refreshToken) {
        if (!refreshToken)
            return true;
        const refreshTokenPayload = parseAccessToken(refreshToken);
        // Subtract grace-period to account for time skew
        const expiryTime = refreshTokenPayload.exp - authEarlyExpiryTime;
        return !refreshTokenPayload || this.estimatedServerTime() >= expiryTime;
    }
    accessTokenExpiresInMs() {
        const expiryTime = this.accessTokenExpiresAt - authEarlyExpiryTime;
        return (expiryTime - this.estimatedServerTime()) * 1000;
    }
}
/**
 * Storage singleton for the auth data.
 *
 * @warning Use ONLY in the AuthManager.
 */
export const authStorage = new AuthStorage();
//# sourceMappingURL=authStorage.js.map